import FF from "@/components/forms/FormField.module.css";
import TF from "@/components/forms/TextField.module.css";

import { controllerProps, formPropsToAriaProps } from "@/components/forms/ariaController.tsx";
import { CreateFormAction } from "@/components/forms/CreateFormAction.tsx";
import { FormField } from "@/components/forms/FormField.tsx";
import { IconRadio, type IconRadioContentProps } from "@/components/forms/IconRadio.tsx";
import { RadioGroupDiv } from "@/components/forms/RadioGroupDiv.tsx";
import { WHookForm } from "@/components/forms/WHookForm.tsx";
import { WRadioGroup } from "@/components/forms/WRadioGroup.tsx";
import { WTextField, type WTextFieldProps } from "@/components/forms/WTextField.tsx";
import { HeroBlock } from "@/components/HeroBlock.tsx";
import { Separator } from "@/components/Separator.tsx";
import { getResourceTypeName } from "@/modules/api/resourceTypes.tsx";
import { useStandardMutation } from "@/modules/api/useStandardMutation.ts";
import { BillingDiscountNotice } from "@/modules/billing/BillingDiscountNotice.tsx";
import { BillingAccountField } from "@/modules/billing/forms/BillingAccountField.tsx";
import { LocationFieldCustom } from "@/modules/location/LocationField.tsx";
import { useLocationsForType } from "@/modules/location/locationQuery.ts";
import { commitmentCreateMutation, newLeaseCommitmentBody } from "@/modules/metal/commitment/commitmentApi.ts";
import { leaseLink, leaseUuidLink } from "@/modules/metal/links.ts";
import { type MetalCreateInputs, MetalCreateViewModel } from "@/modules/metal/metalCreateViewModel.ts";
import { metalOsQueryAtom } from "@/modules/metal/metalOsQuery.ts";
import { leaseCreateMutation } from "@/modules/metal/metalQuery.ts";
import { specLocationQueryAtom } from "@/modules/metal/metalSpecQuery.ts";
import { MetalOfferSection } from "@/modules/metal/offer/MetalOfferSection.tsx";
import { useMetalOffers } from "@/modules/metal/offer/useMetalOffers.ts";
import { MetalOsSelect } from "@/modules/metal/os/MetalOsSelect.tsx";
import { getSpecsForLocation } from "@/modules/metal/spec/spec.ts";
import { SpecRadio } from "@/modules/metal/spec/SpecRadio.tsx";
import { pricesAtom } from "@/modules/pricing/pricingQuery.ts";
import { getBareMetalSpecPrice } from "@/modules/pricing/resourcePricing.ts";
import { cn } from "@/utils/classNames.ts";
import { castSetter } from "@/utils/getField.tsx";
import { useSuspenseQueryAtom } from "@/utils/query/useSuspenseQueryAtom.ts";
import { useOnce } from "@/utils/react/useOnce.ts";
import { useGenerateUuid } from "@/utils/useGenerateUuid.ts";
import { Trans, useLingui } from "@lingui/react/macro";
import { Navigate } from "@tanstack/react-router";
import type { MetalLeaseCreateBody } from "@warrenio/api-spec/spec.oats.gen";
import { notNull } from "@warrenio/utils/notNull";
import { type ReactNode, useMemo } from "react";
import { Input } from "react-aria-components";
import { Controller, type SubmitHandler, useForm, useFormContext } from "react-hook-form";
import { pipe, reverse, sortBy } from "remeda";

interface ItemType extends IconRadioContentProps {
    description: ReactNode;
}

const availableTypes = [
    {
        id: "bare_metal",
        title: getResourceTypeName("bare_metal"),
        icon: "jp-host-icon",
        description: "",
    },
] as const satisfies ItemType[];

interface MetalCreateProps {
    inputs?: Partial<MetalCreateInputs>;
}

export function MetalCreate(props: MetalCreateProps) {
    return <MetalCreateForm {...props} />;
}

/** XXX: Locations are completely broken, view model defaults and values are not updated/constrained on location change */
const LOCATIONS_ARE_BROKEN = true;

function MetalCreateForm({ inputs }: MetalCreateProps) {
    //#region Hooks
    const { t } = useLingui();
    const allImages = useSuspenseQueryAtom(metalOsQueryAtom);
    const priceList = useSuspenseQueryAtom(pricesAtom);

    const mutation = useStandardMutation(leaseCreateMutation);
    const mutationCommitment = useStandardMutation(commitmentCreateMutation);

    const locations = useLocationsForType("bare_metal");
    const specs = useSuspenseQueryAtom(specLocationQueryAtom(locations.defaultLocation));

    const requestUuid = useGenerateUuid(undefined);

    const viewModel = useOnce(() => new MetalCreateViewModel(allImages, locations, [...specs.values()]));

    const defaultValues = useMemo(() => {
        return {
            ...viewModel.getDefaultValues(inputs),
            ...inputs,
        };
    }, [viewModel, inputs]);

    /// Form
    const form = useForm<MetalCreateInputs>({
        disabled: mutation.isPending,

        mode: "onBlur",
        reValidateMode: "onBlur",

        defaultValues,
    });

    //#endregion Hooks

    const { watch, control, setValue, getValues } = form;

    viewModel.attachGetterAndSetter(getValues, castSetter(setValue));

    const locationSpecs = getSpecsForLocation([...specs.values()], watch("location"));
    const locationReadyOsIds = locationSpecs.flatMap((item) => item.ready_os_ids);

    // NB: This can be unset when no spec is available
    const spec_uuid = watch("spec_uuid");
    const spec = locationSpecs.find((item) => item.uuid === spec_uuid);

    const offers = useMetalOffers({
        location: watch("location"),
        specUuid: spec_uuid,
        billingAccountId: watch("billing_account_id"),
        includeHourly: true,
    });
    const { getEligibility } = offers;

    const eligibility = getEligibility(watch("period"));

    if (mutation.isSuccess) {
        return <Navigate {...leaseLink(mutation.data)} />;
    } else if (mutationCommitment.isSuccess) {
        // XXX: This is all sorts of bad:
        // - `mutationCommitment` is not returning a proper wrapper type with `location`
        // - the lease lookup is overcomplicated due to API shape
        const leaseUuid = notNull(
            mutationCommitment.data.coverages.find((c) => c.temporal_status === "current"),
        ).metal_lease_uuid;
        return <Navigate {...leaseUuidLink(leaseUuid, mutationCommitment.variables.location)} />;
    }

    const onSubmit: SubmitHandler<MetalCreateInputs> = async (inputs) => {
        const { location, ...rest } = inputs;

        if (rest.period) {
            await mutationCommitment.mutateAsync({
                body: newLeaseCommitmentBody({
                    period_hours: rest.period,
                    billing_account_id: notNull(rest.billing_account_id),
                    requested_os_id: notNull(rest.requested_os_id),
                    machine_spec_uuid: notNull(rest.spec_uuid),
                    display_name: rest.display_name,
                }),
                location,
            });
        } else {
            const body: MetalLeaseCreateBody = {
                display_name: rest.display_name,
                requested_os_id: notNull(rest.requested_os_id),
                spec_uuid: notNull(rest.spec_uuid),
                billing_account_id: notNull(rest.billing_account_id),
            };

            await mutation.mutateAsync({
                requestUuid,
                inputs,
                body,
                location,
            });
        }
    };

    const sortedImages = {
        ...allImages,
        os: pipe(
            allImages.os,
            // Newer versions first (assume they are last in the list)
            reverse(),
            // Available OSes first
            sortBy((item) =>
                locationReadyOsIds.includes(item.os_id) ? (spec?.ready_os_ids.includes(item.os_id) ? 1 : 2) : 3,
            ),
        ),
    };

    const readyOsIds = spec ? spec.ready_os_ids : locationReadyOsIds;

    return (
        <WHookForm form={form} onSubmit={onSubmit}>
            <CreateHero />

            <WRadioGroup label={t`Type`} isRequired value={availableTypes[0].id}>
                {availableTypes.map((item) => (
                    <IconRadio key={item.id} {...item} />
                ))}
            </WRadioGroup>

            <FormField wide label={t`OS`} isRequired>
                <RadioGroupDiv>
                    <Controller
                        control={control}
                        name="requested_os_id"
                        render={({ field }) => (
                            <MetalOsSelect
                                data={sortedImages}
                                ready_os_ids={readyOsIds}
                                value={field.value ?? undefined}
                                onChange={viewModel.onChangeOs}
                            />
                        )}
                    />
                </RadioGroupDiv>
            </FormField>

            {!LOCATIONS_ARE_BROKEN && locations.showLocations && (
                <Controller
                    control={control}
                    name="location"
                    render={({ field }) => (
                        <LocationFieldCustom
                            value={field.value}
                            onChange={viewModel.onLocationChange}
                            locations={locations}
                        />
                    )}
                />
            )}

            <Controller
                control={control}
                name="spec_uuid"
                render={({ field }) => (
                    <WRadioGroup
                        wide
                        label={t`Server class`}
                        isRequired
                        value={field.value}
                        onChange={viewModel.onSpecChange}
                    >
                        {locationSpecs
                            // XXX: Backward compat, remove
                            .filter((item) => item.is_visible !== false)
                            .map((item) => (
                                <SpecRadio
                                    wide
                                    icon="jp-cluster-icon"
                                    value={item.uuid}
                                    key={item.uuid}
                                    price={getBareMetalSpecPrice(priceList, watch("location"), item.uuid)}
                                    {...item}
                                />
                            ))}
                    </WRadioGroup>
                )}
            />

            <FormField wide label=" " className="-mt-3">
                {!!watch("billing_account_id") && (
                    <BillingDiscountNotice billingAccountId={watch("billing_account_id")!} />
                )}
            </FormField>

            <Separator />

            <MetalNameField isRequired />

            <Separator />

            {/* NB: Offers belong to a spec, so there is nothing to show (not even the hourly one) without one */}
            {!!spec_uuid && (
                <Controller
                    control={control}
                    name="period"
                    render={({ field, fieldState: { error } }) => (
                        <MetalOfferSection
                            variant="form"
                            {...offers}
                            value={field.value}
                            errorMessage={error?.message}
                            onChange={viewModel.onOfferChange}
                        />
                    )}
                />
            )}

            <BillingAccountField resourceType="bare_metal" isRequired />

            <Separator />

            <CreateFormAction resourceType="bare_metal" isDisabled={eligibility.isBlockedForLimited} />
        </WHookForm>
    );
}

function MetalNameField(textFieldProps: WTextFieldProps) {
    const { t } = useLingui();
    const { control } = useFormContext<MetalCreateInputs>();
    return (
        <Controller
            control={control}
            name="display_name"
            {...controllerProps(textFieldProps)}
            render={(props) => (
                <WTextField label={t`Resource name`} {...formPropsToAriaProps(props)} {...textFieldProps}>
                    <Input ref={props.field.ref} className={cn(FF.FormFieldInput, TF.Input)} />
                </WTextField>
            )}
        />
    );
}

function CreateHero() {
    const { t } = useLingui();
    return (
        <HeroBlock title={t`Create new`} icon="jp-host-icon">
            <Trans>
                Run your applications on Bare Metal Servers for full control, security, and raw performance. Provision a
                dedicated server optimized for your most demanding use cases.
            </Trans>
        </HeroBlock>
    );
}
